import React from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Typography,
  Button,
  Card,
  Switch,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import type { Plan } from '../../services/billingService';

export interface PlanSelectorProps {
  /** Plans already filtered by type (enterprise / non-enterprise) — period filtering is handled internally */
  plans: Plan[];
  plansLoading: boolean;
  plansError: string | null;
  onRetry: () => void;
  selectedIds: string[];
  billingPeriod: 'monthly' | 'annual';
  onBillingPeriodChange?: (period: 'monthly' | 'annual') => void;
  onToggle: (planId: string) => void;
  /** How to display the price chip on each plan row */
  formatPrice: (plan: Plan) => string;
  /** Multiplier applied to plan.price when computing the running total (default 1; pass personCount for seat-based) */
  priceMultiplier?: number;
  /** Optional content rendered below the total amount inside the summary box */
  totalFooter?: React.ReactNode;
  continueLabel: string;
  onContinue: () => void;
  continueLoading?: boolean;
  /** Show the Continue button even when no items are selected (e.g. base plan is auto-included) */
  alwaysShowContinue?: boolean;
  /** When provided, directly controls whether the Continue button is visible (overrides default logic) */
  showContinue?: boolean;
  /** Per-plan seat quantities; when provided, a quantity field is shown beneath each selected plan row */
  addonQuantities?: Record<string, number>;
  onQuantityChange?: (planId: string, quantity: number) => void;
}

export default function PlanSelector({
  plans,
  plansLoading,
  plansError,
  onRetry,
  selectedIds,
  billingPeriod,
  onToggle,
  formatPrice,
  priceMultiplier = 1,
  totalFooter,
  continueLabel,
  onContinue,
  continueLoading = false,
  alwaysShowContinue = false,
  showContinue,
  addonQuantities,
  onQuantityChange,
}: PlanSelectorProps) {
  const filteredPlans = plans.filter(plan =>
    billingPeriod === 'annual' ? plan.period === 'year' : plan.period === 'month'
  );

  const total = selectedIds.reduce((sum, id) => {
    const plan = filteredPlans.find(p => p.id === id);
    if (!plan) return sum;
    const qty = addonQuantities?.[id] ?? priceMultiplier;
    return sum + plan.price * qty;
  }, 0);

  return (
    <>
      {plansError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {plansError}
        </Alert>
      )}

      {!plansLoading && !plansError && filteredPlans.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No plans available at this time.{' '}
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        </Alert>
      )}

      {plansLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {filteredPlans.map((plan) => {
            const isSelected = selectedIds.includes(plan.id);
            return (
              <Box key={plan.id}>
                <Card
                  onClick={() => onToggle(plan.id)}
                  elevation={0}
                  sx={{
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    bgcolor: isSelected ? 'rgba(86,151,55,0.08)' : 'background.paper',
                    transition: 'border-color 0.15s ease, background-color 0.15s ease',
                    '&:hover': {
                      borderColor: isSelected ? 'primary.dark' : 'primary.light',
                      bgcolor: isSelected ? 'rgba(86,151,55,0.08)' : 'rgba(86,151,55,0.04)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', p: { xs: 2, sm: 2.5 }, gap: { xs: 1, sm: 2 } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: isSelected ? 'primary.main' : 'text.primary',
                          lineHeight: 1.4,
                        }}
                      >
                        {plan.name}
                      </Typography>
                      {plan.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.25, lineHeight: 1.5, display: { xs: 'none', sm: 'block' } }}
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(plan.description) }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: isSelected ? 'primary.main' : 'text.primary',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: 15, sm: 17 },
                        }}
                      >
                        {formatPrice(plan)}
                      </Typography>
                      <Switch
                        checked={isSelected}
                        onChange={() => onToggle(plan.id)}
                        color="primary"
                        size="medium"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Box>
                  </Box>

                  {isSelected && addonQuantities && onQuantityChange && (
                    <Box
                      sx={{
                        px: 2.5,
                        pt: 2,
                        pb: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        borderTop: '1px solid rgba(86, 151, 55, 0.2)',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Typography variant="body2" color="primary.main" fontWeight={500}>
                        Seats:
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={addonQuantities[plan.id] ?? 1}
                        onChange={(e) => {
                          const val = Math.max(1, parseInt(e.target.value) || 1);
                          onQuantityChange(plan.id, val);
                        }}
                        slotProps={{ htmlInput: { min: 1, step: 1 } }}
                        sx={{ width: 90, '& .MuiOutlinedInput-root': { bgcolor: 'white' }, '& .MuiOutlinedInput-input': { textAlign: 'center' } }}
                      />
                    </Box>
                  )}
                </Card>
              </Box>
            );
          })}
        </Box>
      )}

      {selectedIds.length > 0 && (
        <Box
          sx={{
            borderRadius: 2,
            mb: 3,
            bgcolor: 'rgba(86,151,55,0.04)',
            border: '1px solid',
            borderColor: 'rgba(86,151,55,0.12)',
            px: { xs: 2, sm: 3 },
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
              Estimated Total
            </Typography>
            {totalFooter}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 22, sm: 26 }, letterSpacing: '-0.02em', color: 'text.primary', lineHeight: 1 }}>
            ${total.toFixed(2)}
          </Typography>
        </Box>
      )}

      {(showContinue ?? (selectedIds.length > 0 || alwaysShowContinue)) && (
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onContinue}
          disabled={continueLoading}
          sx={{
            py: { xs: 1.5, sm: 1.75 },
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
            '&:hover': { boxShadow: '0 3px 8px rgba(0,0,0,0.16), 0 1px 3px rgba(0,0,0,0.10)', transform: 'translateY(-1px)' },
            transition: 'box-shadow 0.2s cubic-bezier(0.16, 1, 0.3, 1), transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {continueLoading ? <CircularProgress size={24} color="inherit" /> : continueLabel}
        </Button>
      )}
    </>
  );
}
